import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Dialog, DialogContent, IconButton,
  CircularProgress, Avatar, Tooltip, alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import useMarketingLiveRefresh from '../../hooks/useMarketingLiveRefresh';
import {
  STATUS_LABELS, STATUS_ICONS, getStatusChipSx,
} from '../../constants/status';
import { TYPE_LABELS } from '../../constants/type';
import { formatValue } from '../../utils/format';
import { daysUntilQuotationDue } from '../tables/OpportunitiesTable';
import formatThaiDate from '../../../../utils/functions/formatThaiDate';
import { formatEmployeeThaiName } from '../../../../utils/functions/employeeName';
import EmployeeAvatar from '../../../../components/EmployeeAvatar';

// บรรทัดข้อมูลย่อยแบบไอคอน+ข้อความ — ใช้ซ้ำหลายจุดใน ProjectCheckinDetail (ลูกค้า, ประเภทงาน,
// งบประมาณ, วันครบกำหนดยื่นราคา)
function MetaLine({ icon: Icon, text, color = 'text.secondary', bold = false }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Icon sx={{ fontSize: 12, color, flexShrink: 0 }} />
      <Typography variant="caption" color={color} fontWeight={bold ? 700 : 400} sx={{ fontSize: '0.68rem' }}>
        {text}
      </Typography>
    </Stack>
  );
}

// รายละเอียดโปรเจกต์ของ 1 รายการเช็คอิน — ดึงข้อมูลจาก c.marketing_opportunities (join มากับ
// todayCheckinsOverview) ทุก field เป็น optional เพราะ endpoint นี้อาจไม่ join บาง field มาครบ —
// เรนเดอร์เฉพาะ field ที่มีจริงเท่านั้น ไม่โชว์ "-"/undefined ให้ดูรก
function ProjectCheckinDetail({ c }) {
  const opp = c.marketing_opportunities || {};
  const StatusIcon = STATUS_ICONS[opp.status];
  const dueDays = daysUntilQuotationDue(opp);
  const dueUrgent = dueDays !== null && dueDays <= 3;
  const typeLabel = opp.type === 'OTHER' ? (opp.type_other || 'Other') : TYPE_LABELS[opp.type];

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" fontWeight={700} sx={{ overflowWrap: 'break-word' }}>
          {opp.project_name || '-'}
        </Typography>
        {opp.status && (
          <Chip
            icon={StatusIcon ? <StatusIcon sx={{ fontSize: '12px !important' }} /> : undefined}
            label={STATUS_LABELS[opp.status] || opp.status}
            size="small"
            variant="outlined"
            sx={{
              height: 18, fontSize: '0.62rem', fontWeight: 600, '& .MuiChip-label': { px: 0.6 }, ...getStatusChipSx(opp.status, 'outlined'),
            }}
          />
        )}
      </Stack>

      <Stack spacing={0.3} sx={{ mt: 0.4 }}>
        {opp.opportunity_number && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            {opp.opportunity_number}
          </Typography>
        )}
        {opp.contact_name_snapshot && (
          <MetaLine icon={PersonOutlineOutlinedIcon} text={opp.contact_name_snapshot} />
        )}
        {typeLabel && <MetaLine icon={LabelOutlinedIcon} text={typeLabel} />}
        {(opp.budget || opp.budget === 0) && (
          <MetaLine icon={PaymentsOutlinedIcon} text={`งบประมาณ ${formatValue(opp.budget)}`} />
        )}
        {opp.quotation_due_date && (
          <MetaLine
            icon={EventOutlinedIcon}
            color={dueUrgent ? 'error.main' : 'text.secondary'}
            bold={dueUrgent}
            text={`เสนอราคา ${formatThaiDate(opp.quotation_due_date)}${dueDays !== null ? (dueDays >= 0 ? ` · อีก ${dueDays} วัน` : ` · เลยกำหนด ${Math.abs(dueDays)} วัน`) : ''}`}
          />
        )}
      </Stack>

      {c.plan_note ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.4 }}>
          <NoteAltOutlinedIcon sx={{ fontSize: 12, color: 'primary.main', mt: '2px', flexShrink: 0 }} />
          <Typography variant="caption" color="text.primary" sx={{ fontSize: '0.68rem' }}>
            {c.plan_note}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.4, fontSize: '0.68rem' }}>
          ไม่มีบันทึกแผนงาน
        </Typography>
      )}
    </Box>
  );
}

// มุมมอง "วันนี้" ของแท็บ "ภาพรวมการทำงาน" — การ์ดจัดกลุ่มตามพนักงาน (โครงเดียวกับแท็บ "งานของ
// ผู้รับผิดชอบ") แต่แหล่งข้อมูลเป็นเช็คอินวันนี้แทน — คนที่ไม่เช็คอินวันนี้เลยจะไม่โผล่ในนี้เลย
export function TodayCheckinsView({ employeeById }) {
  const dispatch = useDispatch();
  const [checkins, setCheckins] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dispatch(actions.todayCheckinsOverview())
      .then((data) => { if (!cancelled) setCheckins(data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch]);

  // เห็นเช็คอินใหม่ของคนอื่นเอง (เงียบ ไม่โชว์ spinner/error)
  useMarketingLiveRefresh(async () => {
    const data = await dispatch(actions.todayCheckinsOverview({ silent: true }));
    const next = data || [];
    setCheckins((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, { enabled: checkins !== null });

  const groups = useMemo(() => {
    const map = {};
    (checkins || []).forEach((c) => {
      if (!map[c.employee_id]) map[c.employee_id] = [];
      map[c.employee_id].push(c);
    });
    return Object.entries(map)
      .map(([employeeId, items]) => {
        const emp = employeeById[employeeId];
        return {
          employeeId,
          employee: emp,
          name: emp ? formatEmployeeThaiName(emp) : '(ไม่ทราบชื่อ)',
          department: emp?.department?.name,
          role: emp?.role?.name,
          items,
        };
      })
      .sort((a, b) => b.items.length - a.items.length);
  }, [checkins, employeeById]);

  const selectedGroup = groups.find((g) => g.employeeId === selectedEmployeeId) || null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <EventBusyOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
        <Typography variant="body2">วันนี้ยังไม่มีใครเช็คอิน</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 2, alignItems: 'start' }}>
        {groups.map((group) => (
          <Paper
            key={group.employeeId}
            elevation={0}
            onClick={() => setSelectedEmployeeId(group.employeeId)}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 0.5,
              transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: (theme) => (theme.palette.mode === 'dark'
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(15,23,42,0.1)'),
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Tooltip title="เช็คอินวันนี้แล้ว">
              <Box sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 9,
                height: 9,
                borderRadius: '50%',
                bgcolor: 'success.main',
                boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.success.main, 0.18)}`,
              }}
              />
            </Tooltip>

            <EmployeeAvatar
              employee={group.employee}
              size={60}
              sx={{
                mb: 0.5,
                border: '3px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
              }}
            />
            <Typography fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>{group.name}</Typography>
            {(group.department || group.role) && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '100%' }}>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {[group.department, group.role].filter(Boolean).join(' · ')}
                </Typography>
              </Stack>
            )}
            <Chip
              icon={<CheckCircleOutlinedIcon sx={{ fontSize: '14px !important' }} />}
              label={`${group.items.length} โปรเจกต์`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 1, fontWeight: 700 }}
            />

            <Stack spacing={0.75} sx={{ mt: 1.25, width: '100%' }}>
              {group.items.map((c) => (
                <Box
                  key={c.id}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    textAlign: 'left',
                  }}
                >
                  <ProjectCheckinDetail c={c} />
                </Box>
              ))}
            </Stack>
          </Paper>
        ))}
      </Box>

      <Dialog
        open={!!selectedGroup}
        onClose={() => setSelectedEmployeeId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 52%, #3b82f6 100%)',
            color: '#fff',
          }}
        >
          <EmployeeAvatar
            employee={selectedGroup?.employee}
            size={48}
            sx={{ border: '2px solid rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)', letterSpacing: 0.5 }}>
              เช็คอินวันนี้
              {selectedGroup && (selectedGroup.department || selectedGroup.role)
                ? ` · ${[selectedGroup.department, selectedGroup.role].filter(Boolean).join(' · ')}`
                : ''}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {selectedGroup?.name}
            </Typography>
          </Box>
          <Chip
            label={`${selectedGroup?.items.length || 0} โปรเจกต์`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700 }}
          />
          <IconButton
            onClick={() => setSelectedEmployeeId(null)}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2, bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f8fafc' : 'background.default') }}>
          <Stack spacing={1.25}>
            {selectedGroup?.items.map((c) => (
              <Paper
                key={c.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  gap: 1.25,
                  alignItems: 'flex-start',
                }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), color: 'primary.main', flexShrink: 0 }}>
                  <BusinessCenterOutlinedIcon fontSize="small" />
                </Avatar>
                <ProjectCheckinDetail c={c} />
              </Paper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TodayCheckinsView;
